import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Plus, MapPin, CalendarDays, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppHeader from "@/components/AppHeader";

const STATUS_STYLES = {
  new: "bg-slate-100 text-slate-600 border-slate-300",
  matching: "bg-orange-50 text-flame border-flame",
  offers_received: "bg-blue-50 text-blue-600 border-blue-300",
  appointment_pending: "bg-amber-50 text-amber-600 border-amber-300",
  booked: "bg-green-50 text-green-700 border-green-400",
  in_progress: "bg-blue-50 text-blue-700 border-blue-400",
  completed: "bg-green-50 text-green-700 border-green-400",
  cancelled: "bg-slate-100 text-slate-500 border-slate-300",
  expired: "bg-slate-100 text-slate-400 border-slate-300",
  open: "bg-orange-50 text-flame border-flame",
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState(null);

  const load = useCallback(() => {
    api.get("/requests/my").then((r) => setRequests(r.data)).catch(() => setRequests([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  const acceptOffer = async (offerId) => {
    try {
      await api.post(`/offers/${offerId}/accept`);
      toast.success("Offer accepted — your project is booked");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not accept offer");
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F5F7]">
      <AppHeader />
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <p className="uppercase tracking-[0.25em] text-flame font-heading font-bold text-sm mb-2">My Dashboard</p>
            <h1 data-testid="dashboard-heading" className="font-heading font-black tracking-tighter text-navy text-4xl">
              Welcome, {user?.name?.split(" ")[0]}
            </h1>
          </div>
          <button data-testid="dashboard-new-request-btn" onClick={() => navigate("/request/new")}
            className="flex items-center gap-2 bg-flame hover:bg-flame-dark text-white font-heading font-bold uppercase tracking-wider text-sm px-6 py-4 transition-colors">
            <Plus size={16} /> New Request
          </button>
        </div>

        {requests === null ? (
          <p className="text-slate-400 text-sm">Loading your requests…</p>
        ) : requests.length === 0 ? (
          <div data-testid="dashboard-empty" className="bg-white border border-border p-12 text-center">
            <p className="font-heading font-bold text-navy text-xl">No service requests yet</p>
            <p className="text-slate-500 text-sm mt-2">Post your first project and let local pros come to you.</p>
            <button data-testid="dashboard-empty-cta" onClick={() => navigate("/request/new")}
              className="mt-6 bg-navy hover:bg-navy-light text-white font-heading font-bold uppercase tracking-wider text-sm px-8 py-4 transition-colors">
              Post a Request
            </button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="dashboard-request-list">
            {requests.map((r) => (
              <div key={r.id} data-testid={`request-card-${r.id}`} className="bg-white border border-border p-6">
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-heading font-black text-navy text-xl tracking-tight">{r.category_name}</h3>
                      {r.is_emergency && (
                        <span className="flex items-center gap-1 bg-red-600 text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1">
                          <AlertTriangle size={11} /> Urgent / Emergency
                        </span>
                      )}
                    </div>
                    <p className="text-slate-500 text-sm mt-2 line-clamp-2 max-w-xl">{r.description}</p>
                  </div>
                  <span className={`text-[11px] font-bold uppercase tracking-widest border px-3 py-1 ${STATUS_STYLES[r.status] || STATUS_STYLES.new}`}>
                    {r.status.replace(/_/g, " ")}
                  </span>
                </div>
                <div className="flex items-center gap-6 mt-4 text-xs text-slate-400 flex-wrap">
                  <span className="flex items-center gap-1.5"><MapPin size={13} className="text-flame" /> {r.city}, {r.zip_code}</span>
                  {r.preferred_date && (
                    <span className="flex items-center gap-1.5">
                      <CalendarDays size={13} className="text-flame" /> {r.preferred_date}
                      {r.estimate_method === "onsite" && r.status !== "booked" && " (preferred, awaiting confirmation)"}
                    </span>
                  )}
                  <span>{r.budget_range}</span>
                  {r.leads_sent > 0 && <span>{r.leads_sent} pro{r.leads_sent > 1 ? "s" : ""} notified</span>}
                  {r.photo_ids?.length > 0 && <span>{r.photo_ids.length} photo{r.photo_ids.length > 1 ? "s" : ""}</span>}
                </div>
                {r.offers?.length > 0 && (
                  <div className="mt-5 border-t border-border pt-4 space-y-2" data-testid={`offers-list-${r.id}`}>
                    <p className="text-[11px] font-bold uppercase tracking-widest text-navy">Offers received ({r.offers.length})</p>
                    {r.offers.map((o) => (
                      <div key={o.id} className="flex items-center justify-between gap-3 bg-[#F4F5F7] border border-border px-4 py-3">
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-navy">
                            {o.company_name}
                            {o.amount != null && <span className="text-flame font-heading"> · ${o.amount.toLocaleString()}</span>}
                          </p>
                          <p className="text-xs text-slate-500 truncate">{o.message}</p>
                        </div>
                        {o.status === "accepted" ? (
                          <span className="flex items-center gap-1 text-green-700 text-xs font-bold shrink-0">
                            <CheckCircle2 size={14} /> Accepted
                          </span>
                        ) : r.status !== "booked" && o.status === "submitted" ? (
                          <button data-testid={`accept-offer-${o.id}`} onClick={() => acceptOffer(o.id)}
                            className="shrink-0 bg-navy hover:bg-navy-light text-white text-[11px] font-heading font-bold uppercase tracking-wider px-4 py-2 transition-colors">
                            Accept
                          </button>
                        ) : (
                          <span className="text-slate-400 text-xs shrink-0">Closed</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
